/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useAppDispatch} from 'helpers/store'
import {closeModal, closeSecondaryModal} from 'helpers/actions'
import UiFormModalShell from 'components/Forms/UiFormModalShell'
import UiFormActionsBar from 'components/Forms/UiFormActionsBar'
import {Offer} from 'types/entities/Offer'
import {OffersAction} from 'store/offers/OffersAction'
import {toast} from 'utils/toastUtils'
import Dates from 'utils/Dates'
import {calculateOfferValidUntil} from 'components/Offers/forms/OfferBasicFormUtils'
import UiFormLabel from 'components/Ui/UiFormLabel'
import {DatePicker} from 'antd'
import {DATE_FORMATS} from "constants/DateFormats";
import {AntdPickerConfigProvider} from 'components/Ui/AntdPickerWrapper'

interface EditOfferDateModalProps {
    offer: Offer
    onSuccess?: () => void
    isSecondary?: boolean
}

const EditOfferDateModal: React.FC<EditOfferDateModalProps> = ({ offer, onSuccess, isSecondary = false }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [dateTime, setDateTime] = useState(
        offer.packagesCart?.date
            ? Dates.dayjs(
                  `${offer.packagesCart.date} ${String(offer.packagesCart?.hour ?? 0).padStart(2, '0')}:${String(
                      offer.packagesCart?.minute ?? 0
                  ).padStart(2, '0')}`
              )
            : null
    )
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSave = async (closeAfterSave: boolean = true) => {
        setIsSubmitting(true)
        try {
            if (!dateTime) {
                toast.error(t('Please select a date and time'))
                setIsSubmitting(false)
                return
            }

            const nextEventDate = dateTime.format('YYYY-MM-DD')
            const referenceDate = offer.created_at ? new Date(offer.created_at) : new Date()
            const nextValidUntil = calculateOfferValidUntil(dateTime.toDate(), referenceDate)

            await dispatch(
                OffersAction.setOfferDate({
                    offer_id: offer.id,
                    date: nextEventDate,
                    time: dateTime.format('HH:mm'),
                })
            )

            await dispatch(
                OffersAction.updateOffer({
                    id: offer.id,
                    seller_id: offer.seller_id,
                    valid_until: `${nextValidUntil} 00:00:00`,
                })
            )

            toast.success(t('Event date updated'))
            if (closeAfterSave) {
                if (isSecondary) {
                    dispatch(closeSecondaryModal())
                } else {
                    dispatch(closeModal())
                }
            } else {
                setIsSubmitting(false)
            }
            if (onSuccess) onSuccess()
        } catch (e: any) {
            console.error(e)
            toast.error(t('Failed to update event date'))
            setIsSubmitting(false)
        }
    }

    return (
        <UiFormModalShell
            className="EditOfferDateModal"
            title={t('Edit Event Date')}
            showHeader
            footer={
                <UiFormActionsBar
                    primaryText={t('Save')}
                    onPrimary={() => handleSave(false)}
                    saveAndCloseText={t('Save and close')}
                    onSaveAndClose={() => handleSave(true)}
                    loading={isSubmitting}
                />
            }
        >
            <div className="p-4 flex flex-col gap-6">
                <div className="flex flex-col gap-3">
                    <UiFormLabel>{t('Event date')}</UiFormLabel>
                    <AntdPickerConfigProvider>
                        <DatePicker
                            showTime={{
                                format: 'HH:mm',
                                hideDisabledOptions: true,
                            }}
							minuteStep={10}
                            disabledTime={() => ({ disabledHours: () => [0, 1, 2, 3, 4, 5] })}
                            format={DATE_FORMATS.DATE_TIME}
                            value={dateTime}
                            onChange={setDateTime}
                            needConfirm={false}
                            style={{ width: '100%' }}
                        />
                    </AntdPickerConfigProvider>
                </div>
            </div>
        </UiFormModalShell>
    )
}

export default EditOfferDateModal
